import { useEffect } from "react";
import { Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { NavigationRail } from "../components/navigation/NavigationRail.js";
import { CommandPalette } from "../components/common/CommandPalette.js";
import { wsClient } from "../api/ws.js";
import { useFeedbackStore } from "../stores/feedback.js";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts.js";
import { useNotifications } from "../hooks/useNotifications.js";
import { DEMO_MODE } from "../demo/flag.js";
import { DemoController } from "../demo/DemoController.js";

export function RootLayout() {
  const addMessage = useFeedbackStore((s) => s.addMessage);
  const addPendingInput = useFeedbackStore((s) => s.addPendingInput);

  // Connect WebSocket at root level (persists across route changes)
  useEffect(() => {
    wsClient.connect();

    const unsub = wsClient.onMessage((data) => {
      // EventBus event frames are consumed by the pixel-world bridge, not the
      // MFP feedback store — skip them here so they don't pollute the feed.
      if (data?.message?._type === "event") return;

      if (data.type === "feedback" && data.message) {
        const msg = data.message;
        addMessage({
          id: msg.id,
          type: msg.type,
          source: msg.source,
          payload: msg.payload,
          timestamp: msg.timestamp,
          runId: msg._meta?.runId,
        });

        if (msg.type === "request-for-input") {
          addPendingInput({
            ...msg.payload,
            feedbackMessageId: msg.id,
            runId: msg._meta?.runId,
          });
        }
      }
    });

    return () => {
      // Only remove our handler. The shared client is a singleton meant to
      // persist for the app's lifetime; calling close() here would latch it
      // permanently shut under React StrictMode's mount→unmount→mount cycle.
      unsub();
    };
  }, [addMessage, addPendingInput]);

  // Register keyboard shortcuts & notifications
  useKeyboardShortcuts();
  useNotifications();

  return (
    <div className="flex h-screen bg-surface overflow-hidden">
      {DEMO_MODE && <DemoController />}
      <NavigationRail />
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
      <CommandPalette />
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#111113",
            color: "#e4e4e7",
            border: "1px solid rgba(39, 39, 42, 0.3)",
            borderRadius: "12px",
            fontSize: "13px",
          },
        }}
        theme="dark"
      />
    </div>
  );
}
